import * as React from "react";
import axios from "axios";
import {
  Key,
  Copy,
  RefreshCw,
  Trash2,
  Check,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardContent,
  CardDescription,
  CardTitle,
} from "./card";
import { Button } from "./button";
import { Text } from "./typography";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./alert-dialog";
import { API_BASE_URL } from '../../lib/api';

/**
 * McpApiKeySection - API Key Management for MCP Integration
 *
 * Allows users to generate, view (masked), copy, regenerate, and revoke
 * API keys for AI agent (Claude Desktop/Claude Code) access.
 */
export function McpApiKeySection() {
  // State
  const [keyInfo, setKeyInfo] = React.useState(null);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [showConfig, setShowConfig] = React.useState(false);

  // Dialog states
  const [revokeDialogOpen, setRevokeDialogOpen] = React.useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = React.useState(false);

  // Fetch API key info on mount
  React.useEffect(() => {
    fetchApiKeyInfo();
  }, []);

  const fetchApiKeyInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/mcp/api-key`, {
        withCredentials: true,
      });
      setKeyInfo(response.data);
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const response = await axios.post(
        `${API_BASE_URL}/mcp/api-key`,
        {},
        { withCredentials: true }
      );
      // Store the newly generated key (only shown once!)
      setNewlyGeneratedKey(response.data.apiKey);
      // Refresh key info to get masked version
      await fetchApiKeyInfo();
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      setError(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeKey = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await axios.delete(`${API_BASE_URL}/mcp/api-key`, {
        withCredentials: true,
      });
      setKeyInfo({ hasKey: false });
      setNewlyGeneratedKey(null);
      setRevokeDialogOpen(false);
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      setError(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateKey = async () => {
    setRegenerateDialogOpen(false);
    await handleGenerateKey();
  };

  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError("Failed to copy to clipboard");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Generate Claude Desktop config snippet
  const getConfigSnippet = (apiKey) => {
    const key = apiKey || "YOUR_API_KEY_HERE";
    return `{
  "mcpServers": {
    "portfolio-tracker": {
      "command": "node",
      "args": ["/path/to/portfolio-tracker/mcp/dist/index.js"],
      "env": {
        "PORTFOLIO_API_KEY": "${key}",
        "PORTFOLIO_API_URL": "https://api.lipiit.eu"
      }
    }
  }
}`;
  };

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">MCP Integration</CardTitle>
          </div>
          <CardDescription>
            Loading API key information...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasKey = keyInfo?.hasKey;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">MCP Integration</CardTitle>
          </div>
          <CardDescription>
            Connect AI agents (Claude Desktop, Claude Code) to access your
            portfolio data using the Model Context Protocol.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* No API Key State */}
          {!hasKey && !newlyGeneratedKey && (
            <div className="space-y-4">
              <div className="rounded-lg border border-dashed border-border p-6 text-center">
                <Key className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <Text variant="secondary" className="mb-4">
                  No API key generated yet. Generate one to enable AI agent
                  access to your portfolio.
                </Text>
                <Button
                  onClick={handleGenerateKey}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key className="mr-2 h-4 w-4" />
                      Generate API Key
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Newly Generated Key (shown only once) */}
          {newlyGeneratedKey && (
            <div className="space-y-4">
              <div className="rounded-lg border-2 border-success bg-success/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="h-5 w-5 text-success" />
                  <Text className="font-semibold text-success">
                    API Key Generated Successfully
                  </Text>
                </div>
                <div className="rounded-md bg-background border border-border p-3 mb-3">
                  <code className="text-sm font-mono break-all">
                    {newlyGeneratedKey}
                  </code>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyKey(newlyGeneratedKey)}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy to Clipboard
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex items-start gap-2 text-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <Text variant="secondary" size="sm">
                    <span className="font-semibold text-warning">Important:</span>{" "}
                    This is the only time you will see the full API key.
                    Copy it now and store it securely.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Existing Key Info */}
          {hasKey && (
            <div className="space-y-4">
              {/* Key Status */}
              <div className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Text className="font-medium">API Key</Text>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-success/10 text-success">
                    <span className="h-1.5 w-1.5 rounded-full bg-success" />
                    Active
                  </span>
                </div>

                {/* Masked Key Display */}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-muted px-3 py-2 rounded-md">
                    {keyInfo.maskedKey || "mcp_sk_****...****"}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopyKey(keyInfo.maskedKey)}
                    title="Copy masked key"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Text variant="secondary" size="sm">
                      Created
                    </Text>
                    <Text size="sm">{formatDate(keyInfo.createdAt)}</Text>
                  </div>
                  <div>
                    <Text variant="secondary" size="sm">
                      Last Used
                    </Text>
                    <Text size="sm">
                      {keyInfo.lastUsed
                        ? formatDate(keyInfo.lastUsed)
                        : "Never"}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setRegenerateDialogOpen(true)}
                  disabled={actionLoading}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Regenerate Key
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRevokeDialogOpen(true)}
                  disabled={actionLoading}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Revoke Key
                </Button>
              </div>
            </div>
          )}

          {/* Setup Instructions */}
          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <Text className="font-medium">Claude Desktop Configuration</Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfig(!showConfig)}
              >
                {showConfig ? (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Show
                  </>
                )}
              </Button>
            </div>

            {showConfig && (
              <div className="space-y-3">
                <Text variant="secondary" size="sm">
                  Add this configuration to your Claude Desktop config file
                  (usually at{" "}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    ~/Library/Application Support/Claude/claude_desktop_config.json
                  </code>
                  ):
                </Text>
                <div className="relative">
                  <pre className="text-xs font-mono bg-muted p-3 rounded-md overflow-x-auto">
                    {getConfigSnippet(newlyGeneratedKey)}
                  </pre>
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() =>
                      handleCopyKey(getConfigSnippet(newlyGeneratedKey))
                    }
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <Text variant="secondary" size="sm">
                  Replace{" "}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    /path/to/portfolio-tracker
                  </code>{" "}
                  with the actual path to your installation.
                </Text>
              </div>
            )}

            {/* Documentation Link */}
            <a
              href="https://modelcontextprotocol.io/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Learn more about MCP
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              Are you sure you want to revoke your API key? This will
              immediately disconnect all AI agents using this key. You can
              generate a new key afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleRevokeKey}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Revoke Key"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog
        open={regenerateDialogOpen}
        onOpenChange={setRegenerateDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/10">
                <RefreshCw className="h-5 w-5 text-warning" />
              </div>
              <AlertDialogTitle>Regenerate API Key</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              This will create a new API key and invalidate the current one.
              All AI agents using the old key will need to be updated with the
              new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              Cancel
            </AlertDialogCancel>
            <Button onClick={handleRegenerateKey} disabled={actionLoading}>
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                "Regenerate Key"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default McpApiKeySection;
